import semver from '@/utils/semver'
import useCdcProps from '@/composables/useCdcProps'
import { set } from 'radash'
import {
  enrichProductWithCompliance,
  enrichProductOverridesWithShopOverrides,
} from '@/services/ComplianceService'

import { Product, ProductBase } from '@/types/products.interfaces'
import { ProductSearch, HomeBlock } from '@/types/products.search.interfaces'
import type { Currency } from '@/types/global.interfaces'
import {
  ProductCategoryFromApi,
  ProductMboCategory,
} from '@/types/products.mbo.interfaces'

const { context } = useCdcProps()

export const searchMapper = (
  search: ProductSearch | HomeBlock,
  currency: Currency,
): ProductSearch => {
  const products = search.products.map(
    (product: ProductBase): Product => productMapper(product, currency),
  )

  return {
    products,
    pagination: search.pagination,
    sorting: search.sorting,
    details: search.details,
  }
}

export const categoriesFromApiMapper = (
  categories: ProductCategoryFromApi[],
): Record<string, ProductMboCategory> => {
  const mapper = (
    cat: ProductCategoryFromApi,
    isParent = true,
  ): ProductMboCategory => ({
    isParent,
    childCategoriesId: cat.categories?.map((child) =>
      child.id_category.toString(),
    ),
    id: cat.id_category.toString(),
    parentId: cat.id_parent?.toString(),
    link: cat.link_rewrite,
    name: cat.name,
  })

  //TODO typing
  const data = categories.reduce((list: any, parentCat) => {
    list[parentCat.id_category] = mapper(parentCat)
    parentCat.categories.forEach((childCat) => {
      list[childCat.id_category] = mapper(childCat, false)
    })
    return list
  }, {})
  return data
}

export const blockMapper = (
  blocks: HomeBlock[],
  currency: Currency,
): HomeBlock[] => {
  for (let index = 0; index < blocks.length; index++) {
    blocks[index].products = searchMapper(blocks[index], currency).products
    if (blocks[index].details.color === '')
      blocks[index].details.color = 'highlight'
  }
  return blocks
}

function getPicoUrlForIdProduct(idProduct: number) {
  return `${import.meta.env.VITE_GCLOUD_CDN_URL}/pico/${idProduct}.jpg`
}

/**
 * Map a product to a Product object
 * @param product - The product to map
 * @param currency - The currency to set
 * @returns The mapped product
 */
export function productMapper(product: Product, currency: Currency): Product {
  set(product, 'prices.currency', currency)
  product.product.pico = getPicoUrlForIdProduct(product?.product?.id_product)

  // TODO Remove when period_unit on rbm
  if (
    product.rbm?.min_price !== undefined &&
    product.rbm.min_price.period_unit === undefined
  )
    product.rbm.min_price.period_unit = product.rbm.min_price.period

  // Basic product mapping
  const mappedProduct = {
    ...product,
    category: product.breadcrumb.at(-2),
    categoryParent: product.breadcrumb.at(-3),
    technical_info: {
      ...product.technical_info,
      last_release: semver.getLastRelease(
        product.technical_info.releases,
        context.shop_version,
      ) || product.technical_info.last_release,
    },
  }

  // Enrich with compliance information through the service functions
  return enrichProductWithCompliance(mappedProduct)
}

/**
 * Map a product to a Product object with overrides details
 * Warning: this function processes override data which may impact performance in loops
 * @param product - The product to map
 * @param currency - The currency to set
 * @returns The mapped product
 */
export function productMapperWithOverridesDetails(
  product: Product,
  currency: Currency,
): Product {
  return enrichProductOverridesWithShopOverrides(
    productMapper(product, currency),
  )
}

export const productPurchasedMapper = (product: any) => {
  return {
    product: {
      id_product: parseInt(product.id),
      product_type: 'module',
    },
    product_lang: {
      product_name: product.displayName,
      category_name: product.categoryName,
      product_link: product.url,
      short_description: product.description,
    },
    owner: {
      manufacturer: { name: product.author },
      seller: { is_certified: product.isCertified || 0 },
    },
    gallery: {
      cover: product.img,
    },
    category: {
      id_category: product.categoryId,
      name: product.categoryName,
      english_name: product.categoryEnglishName,
    },
    categoryParent: {
      id_category: product.categoryParentId,
      name: product.categoryParentName,
      english_name: product.categoryParentEnglishName,
    },
    technical_info: {
      last_release: product.version,
      compatible_from: product.compatibility.from,
      compatible_to: product.compatibility.to,
      technical_name: product.name,
    },
    ratings: {
      nb_av_rates: product.avgRate,
      nb_rates: product.nbRates,
    },
    prices: {
      product: {
        original_price: product.price.EUR,
      },
    },
  }
}

export const reviewsMapper = (reviews: any) => {
  return reviews
}
